import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Home, Network, Users, CalendarDays, Receipt, FileText, Inbox, UserCircle2, Settings, LogOut,
  Briefcase, Sparkles, UserMinus, Plane, Wallet, TrendingUp, GraduationCap, BookOpen, CalendarClock, Building2, Grid3x3, LifeBuoy, Gauge, BarChart3, Handshake,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useTenant } from "../lib/tenant";
import LanguageToggle from "./LanguageToggle";
import TenantSwitcher from "./TenantSwitcher";
import api from "../lib/api";
import Logo from "./Logo";

const NavItem = ({ to, icon: Icon, label, testid, badge, navKey, hiddenSet }) => {
  if (navKey && hiddenSet && hiddenSet.has(navKey)) return null;
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-[10px] text-[13.5px] font-medium transition-all ${
          isActive
            ? "bg-[#e2e8f0] text-[#1e293b]"
            : "text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#1e293b]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={17} strokeWidth={1.75} className={`shrink-0 ${isActive ? "text-[#1e293b]" : "text-[#94a3b8] group-hover:text-[#475569]"}`} />
          <span className="flex-1 truncate">{label}</span>
          {badge != null && badge > 0 && (
            <span className="bg-[#475569] text-white text-[10px] rounded-full px-2 py-0.5 shrink-0">{badge}</span>
          )}
        </>
      )}
    </NavLink>
  );
};

const SectionLabel = ({ children }) => (
  <div className="px-3 pt-4 pb-1 text-[10px] font-semibold tracking-[0.12em] uppercase text-[#94a3b8]">
    {children}
  </div>
);

export default function Sidebar() {
  const { user, logout, hasPerm } = useAuth();
  const { t } = useI18n();
  const { tenant } = useTenant();
  // Kept for compatibility with a few UI states — but ALL menu visibility below
  // is driven by permissions, not role.
  const isAdmin = user && ["admin", "hr"].includes(user.role);
  const isSuperAdmin = user && user.role === "superadmin";
  const [pendingOnboarding, setPendingOnboarding] = useState(0);
  const [isManager, setIsManager] = useState(false);
  const [hiddenSet, setHiddenSet] = useState(new Set());
  const [hasLoans, setHasLoans] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (["admin", "hr"].includes(user.role) || user.onboarding_completed === true) return;
    api.get("/onboarding/my-tasks")
      .then((r) => setPendingOnboarding(r.data.filter((t) => t.status === "pending").length))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (hasPerm("cockpit.view")) { setIsManager(true); return; }
    api.get("/cockpit/me/is-manager")
      .then((r) => setIsManager(!!r.data?.is_manager))
      .catch(() => setIsManager(false));
  }, [user, hasPerm]);

  useEffect(() => {
    if (!user) return;
    api.get("/nav-visibility/me")
      .then((r) => setHiddenSet(new Set(r.data?.hidden_items || [])))
      .catch(() => setHiddenSet(new Set()));
  }, [user]);

  // Loans menu visibility: shown if user has view_all (HR/CEO/admin/analyst),
  // or if the user has any loan of their own (view_own).
  useEffect(() => {
    if (!user) return;
    if (hasPerm("loans.view_all")) { setHasLoans(true); return; }
    api.get("/loans?mine=true")
      .then((r) => setHasLoans(Array.isArray(r.data) && r.data.length > 0))
      .catch(() => setHasLoans(false));
  }, [user, hasPerm]);

  return (
    <aside className="hidden lg:flex w-[260px] bg-white border-r border-[#e2e8f0] h-screen sticky top-0 flex-col p-4 gap-1">
      {/* Logo */}
      <div className="flex items-center gap-3 px-2 pt-2 pb-3">
        <Logo variant="mark" className="w-10 h-10 shrink-0" />
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-[#1e293b] leading-tight truncate tracking-wide uppercase">{tenant?.short_name || tenant?.name || "Sagewood"}</div>
          <div className="text-[10px] text-[#94a3b8] tracking-[0.18em] uppercase truncate">{tenant?.tagline || t("nav.tagline")}</div>
        </div>
      </div>

      <div className="border-t border-[#e2e8f0]" />

      <TenantSwitcher className="mx-1 mt-2" />

      {/* Nav */}
      <nav className="flex flex-col gap-0.5 flex-1 overflow-y-auto -mx-1 px-1 mt-1">
        <SectionLabel>{t("nav.section.workspace") || "Workspace"}</SectionLabel>
        <NavItem navKey="dashboard" hiddenSet={hiddenSet} to="/app/dashboard" icon={Home} label={t("nav.dashboard")} testid="nav-dashboard" />
        <NavItem navKey="planner" hiddenSet={hiddenSet} to="/app/planner" icon={CalendarClock} label={t("nav.planner")} testid="nav-planner" />
        <NavItem navKey="pto" hiddenSet={hiddenSet} to="/app/pto" icon={CalendarDays} label={t("nav.pto")} testid="nav-pto" />
        <NavItem navKey="travel" hiddenSet={hiddenSet} to="/app/travel" icon={Plane} label={t("travel.page_title")} testid="nav-travel" />
        <NavItem navKey="reimbursements" hiddenSet={hiddenSet} to="/app/reimbursements" icon={Receipt} label={t("nav.reimb")} testid="nav-reimb" />
        <NavItem navKey="trainings" hiddenSet={hiddenSet} to="/app/trainings" icon={GraduationCap} label={t("nav.trainings")} testid="nav-trainings" />
        {pendingOnboarding > 0 && (
          <NavItem navKey="onboarding" hiddenSet={hiddenSet} to="/app/onboarding" icon={Sparkles} label={t("nav.onboarding")} testid="nav-onboarding" badge={pendingOnboarding} />
        )}

        <SectionLabel>{t("nav.section.people") || "People"}</SectionLabel>
        {isManager && <NavItem navKey="cockpit" hiddenSet={hiddenSet} to="/app/cockpit" icon={Gauge} label={t("nav.cockpit") || "Manager cockpit"} testid="nav-cockpit" />}
        <NavItem navKey="directory" hiddenSet={hiddenSet} to="/app/directory" icon={Users} label={t("nav.directory")} testid="nav-directory" />
        <NavItem navKey="departments" hiddenSet={hiddenSet} to="/app/departments" icon={Building2} label={t("nav.departments")} testid="nav-departments" />
        <NavItem navKey="org-chart" hiddenSet={hiddenSet} to="/app/org-chart" icon={Network} label={t("nav.org")} testid="nav-org" />
        <NavItem navKey="performance" hiddenSet={hiddenSet} to="/app/performance" icon={TrendingUp} label={t("nav.performance")} testid="nav-performance" />
        {hasPerm("hiring.view") && <NavItem navKey="hiring" hiddenSet={hiddenSet} to="/app/hiring" icon={Briefcase} label={t("nav.hiring")} testid="nav-hiring" />}
        {hasPerm("offboarding.view") && <NavItem navKey="offboarding" hiddenSet={hiddenSet} to="/app/offboarding" icon={UserMinus} label={t("nav.offboarding")} testid="nav-offboarding" />}
        {hasPerm("salary_matrix.view") && <NavItem navKey="salary-matrix" hiddenSet={hiddenSet} to="/app/salary-matrix" icon={Grid3x3} label={t("nav.salary_matrix")} testid="nav-salary" />}

        <SectionLabel>{t("nav.section.operations") || "Operations"}</SectionLabel>
        <NavItem navKey="approvals" hiddenSet={hiddenSet} to="/app/approvals" icon={Inbox} label={t("nav.approvals")} testid="nav-approvals" />
        {(hasPerm("reports.request") || ["hr", "admin"].includes(user?.role)) && <NavItem navKey="reports" hiddenSet={hiddenSet} to="/app/reports" icon={BarChart3} label={t("nav.reports")} testid="nav-reports" />}
        <NavItem navKey="loans" hiddenSet={hasLoans ? hiddenSet : new Set([...hiddenSet, "loans"])} to="/app/loans" icon={Wallet} label={t("nav.loans")} testid="nav-loans" />
        <NavItem navKey="documents" hiddenSet={hiddenSet} to="/app/documents" icon={FileText} label={t("nav.docs")} testid="nav-docs" />
        {hasPerm("kb.edit") && <NavItem navKey="knowledge" hiddenSet={hiddenSet} to="/app/knowledge" icon={BookOpen} label={t("nav.knowledge")} testid="nav-knowledge" />}

        <SectionLabel>{t("nav.section.system") || "System"}</SectionLabel>
        <NavItem navKey="copilot" hiddenSet={hiddenSet} to="/app/copilot" icon={Sparkles} label={t("nav.copilot")} testid="nav-copilot" />
        <NavItem navKey="manual" hiddenSet={hiddenSet} to="/app/manual" icon={LifeBuoy} label={t("nav.manual") || "User manual"} testid="nav-manual" />
        <NavItem navKey="profile" hiddenSet={hiddenSet} to="/app/profile" icon={UserCircle2} label={t("nav.profile")} testid="nav-profile" />
        {(hasPerm("admin.manage_config") || hasPerm("admin.manage_users")) && <NavItem to="/app/service-contracts" icon={Handshake} label={t("nav.service_contracts")} testid="nav-service-contracts" />}
        {(hasPerm("admin.manage_config") || hasPerm("admin.manage_users")) && <NavItem to="/app/admin/branding" icon={Settings} label={t("nav.branding")} testid="nav-branding" />}
        {(hasPerm("admin.manage_config") || hasPerm("admin.manage_users") || hasPerm("admin.manage_permissions")) && <NavItem to="/app/admin" icon={Settings} label={t("nav.admin")} testid="nav-admin" />}
        {isSuperAdmin && <NavItem to="/app/superadmin" icon={Settings} label={t("nav.superadmin")} testid="nav-superadmin" />}
      </nav>

      {/* Footer */}
      <div className="flex flex-col gap-3 pt-3 border-t border-[#e2e8f0] mt-2">
        <LanguageToggle className="self-start" />
        {user && (
          <div className="flex items-center gap-3 px-2">
            <div className="w-9 h-9 rounded-full bg-[#e2e8f0] text-[#475569] flex items-center justify-center text-[13px] font-semibold shrink-0">
              {user.name?.split(" ").map((n) => n[0]).slice(0,2).join("")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-[#1e293b] truncate">{user.name}</div>
              <div className="text-[11px] text-[#94a3b8] capitalize truncate">{user.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="text-[#94a3b8] hover:text-[#881a27] transition-colors p-1 shrink-0"
              aria-label={t("nav.logout_label")}
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
